import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowLeft, Search, Plus, X as CloseIcon, Trash2, AlertTriangle } from "lucide-react";
import { ListBuilderPanel } from "@/components/ListBuilderPanel";
import { AddDonorDialog } from "@/components/AddDonorDialog";

/**
 * All Donors — the canonical view: every donor for this client across all
 * lists, with their list memberships shown as chips. From here a donor can
 * be added to another list (a MEMBERSHIP — never a copy), removed from a
 * list (membership only; the donor persists), or permanently deleted (the
 * separate, explicitly-confirmed action).
 */
const fmtMoney = (n) =>
  (Number(n) || 0).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export const AllDonorsTab = ({ onBack = null }) => {
  const [, setSearchParams] = useSearchParams();
  const [donors, setDonors] = useState([]);
  const [lists, setLists] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  // Which donor's "Add to list" picker is open
  const [pickerFor, setPickerFor] = useState(null);
  const [addDonorOpen, setAddDonorOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/caller/all-donors");
      setDonors(data.donors || []);
      setLists(data.lists || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return donors;
    return donors.filter((d) =>
      `${d.first_name} ${d.last_name} ${d.city} ${d.state}`.toLowerCase().includes(needle)
    );
  }, [donors, q]);

  const addToList = async (donor, categoryId) => {
    setBusyId(donor.id);
    try {
      const { data } = await api.post(`/caller/donors/${donor.id}/lists`, { category_id: categoryId });
      toast.success(`${donor.first_name} ${donor.last_name} added to "${data.added_to?.name}"`);
      setPickerFor(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const removeFromList = async (donor, list) => {
    if (!window.confirm(
      `Remove ${donor.first_name} ${donor.last_name} from "${list.name}"?\n\nThis only removes them from that list — their record, notes, and call history are kept.`
    )) return;
    setBusyId(donor.id);
    try {
      await api.delete(`/caller/donors/${donor.id}/lists/${list.id}`);
      toast.success(`Removed from "${list.name}" — donor kept`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  const deleteDonor = async (donor) => {
    const typed = window.prompt(
      `PERMANENTLY delete ${donor.first_name} ${donor.last_name}?\n\nThis erases their record, giving history, and notes from every list. This cannot be undone.\n\nType DELETE to confirm:`
    );
    if (typed !== "DELETE") {
      if (typed !== null) toast.info("Delete cancelled — you must type DELETE exactly.");
      return;
    }
    setBusyId(donor.id);
    try {
      await api.delete(`/caller/donors/${donor.id}?confirm=DELETE`);
      toast.success(`${donor.first_name} ${donor.last_name} permanently deleted`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="max-w-md sm:max-w-lg lg:max-w-3xl mx-auto px-4 lg:px-6 py-4" data-testid="caller-all-donors">
      {onBack && (
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-flag-navy mb-3"
          data-testid="all-donors-back"
        >
          <ArrowLeft className="h-4 w-4" /> Back to call lists
        </button>
      )}
      <div className="mb-1 flex items-baseline justify-between gap-3">
        <h2 className="font-display text-xl sm:text-2xl font-black text-flag-navy">All Donors</h2>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-500" data-testid="all-donors-count">
            {donors.length.toLocaleString()} total
          </span>
          <button
            type="button"
            onClick={() => setAddDonorOpen(true)}
            className="inline-flex items-center gap-1 text-sm font-semibold text-white bg-flag-navy hover:bg-flag-navy/90 px-3 py-1.5 rounded-md"
            data-testid="all-donors-add-btn"
          >
            <Plus className="h-4 w-4" /> Add donor
          </button>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-3">
        Your master directory — one record per person, across every list. Removing from a list never deletes the donor.
      </p>

      {/* List Builder — filter the directory into a new static-snapshot list. */}
      <div className="mb-4">
        <ListBuilderPanel lists={lists} onCreated={load} />
      </div>

      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search name or city…"
          className="pl-9"
          data-testid="all-donors-search"
        />
      </div>

      {loading && <div className="py-10 text-center text-slate-400 text-sm">Loading…</div>}
      {!loading && visible.length === 0 && (
        <div className="py-10 text-center text-slate-400 text-sm" data-testid="all-donors-empty">
          No donors{q ? " match your search" : " yet"}.
        </div>
      )}

      <div className="space-y-2">
        {visible.map((d) => {
          const addable = lists.filter((l) => !d.lists.some((m) => m.id === l.id));
          return (
            <div
              key={d.id}
              className="rounded-xl border border-slate-200 bg-white px-4 py-3"
              data-testid={`all-donors-row-${d.id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-display font-bold text-flag-navy truncate">
                    {d.first_name} {d.last_name}
                    {d.maxed && (
                      <span className="ml-2 align-middle text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-flag-red text-white">Maxed</span>
                    )}
                    {d.consolidation_flagged && (
                      <span
                        className="ml-2 align-middle inline-flex items-center gap-0.5 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-900"
                        title="Possible duplicate — left unmerged on purpose"
                      >
                        <AlertTriangle className="h-3 w-3" /> Check
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {[d.city, d.state].filter(Boolean).join(", ")}
                    {d.total_given > 0
                      ? ` · Total ${fmtMoney(d.total_given)} · Highest ${fmtMoney(d.highest_gift)}`
                      : " · No giving history"}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => deleteDonor(d)}
                  disabled={busyId === d.id}
                  className="shrink-0 text-slate-300 hover:text-flag-red p-1"
                  title="Permanently delete this donor"
                  data-testid={`all-donors-delete-${d.id}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>

              {/* Memberships */}
              <div className="mt-2 flex items-center gap-1.5 flex-wrap">
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">On:</span>
                {d.lists.length === 0 && (
                  <span className="text-xs text-slate-400 italic">no lists</span>
                )}
                {d.lists.map((l) => (
                  <span
                    key={l.id}
                    className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${
                      l.category_type === "maxed"
                        ? "bg-flag-red/10 text-flag-red"
                        : l.category_type === "prospecting_list"
                        ? "bg-amber-100 text-amber-900"
                        : l.category_type === "pac_list"
                        ? "bg-emerald-100 text-emerald-900"
                        : "bg-slate-100 text-slate-700"
                    }`}
                    data-testid={`all-donors-chip-${d.id}-${l.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => setSearchParams({ category_id: l.id, donor_id: d.id })}
                      className="hover:underline focus:outline-none focus:underline cursor-pointer"
                      title={`Open ${d.first_name} ${d.last_name} in "${l.name}"`}
                      data-testid={`all-donors-chip-open-${d.id}-${l.id}`}
                    >
                      {l.name}
                    </button>
                    {l.category_type !== "maxed" && (
                      <button
                        type="button"
                        onClick={() => removeFromList(d, l)}
                        disabled={busyId === d.id}
                        className="hover:text-flag-red"
                        title={`Remove from ${l.name} (keeps the donor)`}
                        data-testid={`all-donors-remove-${d.id}-${l.id}`}
                      >
                        <CloseIcon className="h-3 w-3" />
                      </button>
                    )}
                  </span>
                ))}
                {addable.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setPickerFor(pickerFor === d.id ? null : d.id)}
                    className="inline-flex items-center gap-0.5 text-[11px] font-semibold px-2 py-0.5 rounded-full border border-dashed border-slate-300 text-slate-500 hover:border-flag-navy hover:text-flag-navy"
                    data-testid={`all-donors-add-${d.id}`}
                  >
                    <Plus className="h-3 w-3" /> Add to list
                  </button>
                )}
              </div>
              {pickerFor === d.id && (
                <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid={`all-donors-picker-${d.id}`}>
                  {addable.map((l) => (
                    <button
                      key={l.id}
                      type="button"
                      onClick={() => addToList(d, l.id)}
                      disabled={busyId === d.id}
                      className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-flag-navy text-white hover:opacity-90 disabled:opacity-50"
                      data-testid={`all-donors-pick-${d.id}-${l.id}`}
                    >
                      {l.name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <AddDonorDialog
        open={addDonorOpen}
        onClose={() => setAddDonorOpen(false)}
        lists={lists}
        onCreated={() => { load(); }}
        onJoined={() => { load(); }}
      />
    </div>
  );
};
